import { forwardRef } from "react";
import {
  useFilters,
  useCurrentUser,
  SORT_MODES,
  SORT_LABELS,
} from "../store/filters.js";

type Props = {
  searchRef: React.Ref<HTMLInputElement>;
  totalVisible: number;
  totalAll: number;
  assignees: string[];
  saving: boolean;
  onNewQuote: () => void;
};

export function Toolbar({
  searchRef,
  totalVisible,
  totalAll,
  assignees,
  saving,
  onNewQuote,
}: Props): JSX.Element {
  const f = useFilters();
  const currentUser = useCurrentUser();
  const filtered = totalVisible !== totalAll;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-ink-700 bg-ink-900/70 px-5 py-2 backdrop-blur-sm">
      <SearchBox inputRef={searchRef} value={f.q} onChange={f.setQ} />

      <AssigneeChip
        value={f.assignee}
        currentUser={currentUser}
        assignees={assignees}
        onChange={f.setAssignee}
      />

      <ToggleChip
        label="Priority"
        active={f.priorityOnly}
        color="#ECB22E"
        onClick={f.togglePriority}
      />

      <ToggleChip
        label="Archived"
        active={f.includeArchived}
        color="#7E3A7F"
        onClick={f.toggleArchived}
      />

      <SortChip value={f.sort} onChange={f.setSort} />

      {filtered ||
      f.priorityOnly ||
      f.assignee !== "all" ||
      f.q ||
      f.sort !== "manual" ? (
        <button
          onClick={f.reset}
          className="rounded-md border border-ink-500 px-2 py-1 text-[12px] text-ink-50 hover:border-ink-400 hover:bg-ink-700"
        >
          Reset
        </button>
      ) : null}

      <div className="ml-auto flex items-center gap-3 text-[12px] text-ink-50">
        <span className="tabular-nums">
          {filtered ? (
            <>
              <span className="font-semibold">{totalVisible}</span>
              <span> / {totalAll}</span>
            </>
          ) : (
            <span className="font-semibold">{totalAll}</span>
          )}{" "}
          cards
        </span>
        {saving ? (
          <span className="text-slack-yellow">saving…</span>
        ) : null}
        <button
          onClick={onNewQuote}
          className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1 text-[12px] font-medium uppercase tracking-wider text-ink-50 hover:bg-slack-aubergineLight"
          title="Find an existing customer — open their account or start a new quote. Create a brand-new account if nobody matches."
        >
          Search
        </button>
      </div>
    </div>
  );
}

const SearchBox = forwardRef<
  HTMLInputElement,
  { inputRef: React.Ref<HTMLInputElement>; value: string; onChange: (v: string) => void }
>(function SearchBox({ inputRef, value, onChange }, _ref) {
  return (
    <div className="relative flex items-center">
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-ink-50"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search name, postcode…  (press / )"
        className="w-72 rounded-md border border-ink-500 bg-ink-800 pl-7 pr-7 py-1.5 text-[12px] text-ink-50 outline-none placeholder:text-ink-50 focus:border-slack-aubergineLight"
      />
      {value ? (
        <button
          onClick={() => onChange("")}
          className="absolute right-1.5 text-[16px] leading-none text-ink-50 hover:text-ink-100"
          aria-label="Clear"
        >
          ×
        </button>
      ) : null}
    </div>
  );
});

function SortChip({
  value,
  onChange,
}: {
  value: (typeof SORT_MODES)[number];
  onChange: (s: (typeof SORT_MODES)[number]) => void;
}): JSX.Element {
  const isDefault = value === "manual";
  return (
    <div className="relative inline-flex">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as (typeof SORT_MODES)[number])}
        className={`appearance-none rounded-md border bg-ink-800 py-1 pl-7 pr-6 text-[12px] uppercase tracking-wider text-ink-50 ${
          isDefault
            ? "border-ink-500 hover:border-ink-400"
            : "border-slack-aubergineLight"
        }`}
        title="Sort cards within each column"
      >
        {SORT_MODES.map((m) => (
          <option key={m} value={m}>
            Sort: {SORT_LABELS[m]}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2">
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5 text-ink-50"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M3 6h13M3 12h9M3 18h5" />
          <path d="m17 10 4 4-4 4" />
        </svg>
      </span>
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-50">
        ▾
      </span>
    </div>
  );
}

function ToggleChip({
  label,
  active,
  color,
  onClick,
}: {
  label: string;
  active: boolean;
  color: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-[12px] uppercase tracking-wider text-ink-50 transition-colors ${
        active
          ? "border-transparent"
          : "border-ink-500 hover:border-ink-400 hover:bg-ink-700"
      }`}
      style={active ? { backgroundColor: `${color}33`, borderColor: color } : undefined}
    >
      <span
        className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle"
        style={{ backgroundColor: active ? color : "#8D8799" }}
      />
      {label}
    </button>
  );
}

function AssigneeChip({
  value,
  assignees,
  onChange,
  currentUser,
}: {
  value: string;
  assignees: string[];
  onChange: (v: string) => void;
  currentUser: string;
}): JSX.Element {
  const label =
    value === "all"
      ? "All"
      : value === "mine"
        ? "Mine"
        : value === "unassigned"
          ? "Unassigned"
          : value;
  return (
    <div className="relative inline-flex">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`appearance-none rounded-md border bg-ink-800 py-1 pl-7 pr-6 text-[12px] uppercase tracking-wider text-ink-50 ${
          value !== "all"
            ? "border-slack-aubergineLight"
            : "border-ink-500 hover:border-ink-400"
        }`}
      >
        <option value="all">All</option>
        <option value="mine">
          Mine{currentUser ? ` (${currentUser.split("@")[0]})` : ""}
        </option>
        <option value="unassigned">Unassigned</option>
        {assignees
          .filter((a) => a !== currentUser)
          .map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
      </select>
      <span
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
        aria-hidden
      >
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5 text-ink-50"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </span>
      <span
        className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-50"
        aria-hidden
      >
        ▾
      </span>
      <span className="sr-only">{label}</span>
    </div>
  );
}
